"use client";

import { useState } from "react";

const REACTION_EMOJIS = [
  { key: "love", emoji: "❤️" },
  { key: "haha", emoji: "😂" },
  { key: "celebrate", emoji: "🎉" },
  { key: "wow", emoji: "😮" },
  { key: "sad", emoji: "😢" },
  { key: "fire", emoji: "🔥" },
] as const;

interface ReactionBarProps {
  reactions: Record<string, string[]> | undefined;
  currentUserId: string | undefined;
  onToggleReaction: (emoji: string) => void;
  /** Override for accessible labels (key → localized name). Falls back to the raw emoji key. */
  reactionLabels?: Record<string, string>;
  addReactionAriaLabel?: string;
  /**
   * Optional callback to open a "who reacted" details sheet. When
   * supplied, a small viewer button appears after the picker affordance
   * and the consumer is responsible for rendering the sheet.
   */
  onShowReactors?: () => void;
  showReactorsAriaLabel?: string;
}

/**
 * Displays reaction pills and a picker dropdown.
 * Matches iOS ReactionBar: 6 emoji types, toggle on/off, pill display.
 */
export function ReactionBar({
  reactions,
  currentUserId,
  onToggleReaction,
  reactionLabels,
  addReactionAriaLabel = "Add reaction",
  onShowReactors,
  showReactorsAriaLabel = "Who reacted",
}: ReactionBarProps) {
  const [showPicker, setShowPicker] = useState(false);

  // Build active reactions (emoji key → user IDs)
  const activeReactions = REACTION_EMOJIS
    .map(({ key, emoji }) => ({
      key,
      emoji,
      users: reactions?.[key] ?? [],
    }))
    .filter((r) => r.users.length > 0);

  const handlePickerToggle = (emojiKey: string) => {
    onToggleReaction(emojiKey);
    setShowPicker(false);
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {/* Active reaction pills */}
      {activeReactions.map(({ key, emoji, users }) => {
        const isActive = currentUserId ? users.includes(currentUserId) : false;

        return (
          <button
            key={key}
            onClick={() => onToggleReaction(key)}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors ${
              isActive
                ? "bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400"
                : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/15"
            }`}
          >
            <span>{emoji}</span>
            <span>{users.length}</span>
          </button>
        );
      })}

      {/* "Who reacted" viewer — only shown when handler supplied
          and at least one reaction exists. */}
      {onShowReactors && activeReactions.length > 0 && (
        <button
          type="button"
          onClick={onShowReactors}
          aria-label={showReactorsAriaLabel}
          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 dark:bg-white/10 text-gray-400 hover:bg-gray-200 dark:hover:bg-white/15 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
      )}

      {/* Add reaction button */}
      <div className="relative">
        <button
          onClick={() => setShowPicker(!showPicker)}
          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 dark:bg-white/10 text-gray-400 hover:bg-gray-200 dark:hover:bg-white/15 transition-colors text-sm"
          aria-label={addReactionAriaLabel}
        >
          +
        </button>

        {/* Picker dropdown */}
        {showPicker && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowPicker(false)}
            />
            <div className="absolute bottom-full left-0 mb-1 z-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-white/10 p-1.5 flex gap-0.5">
              {REACTION_EMOJIS.map(({ key, emoji }) => (
                <button
                  key={key}
                  onClick={() => handlePickerToggle(key)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-colors text-lg"
                  title={reactionLabels?.[key] ?? key}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
